import { createContext, Provider } from 'preact'
import { useContext } from 'preact/hooks'

/**
 * Convenience function to create a context hook and provider for a given data type
 * @generic T The data type of the context
 * @returns A tuple of the context hook and provider
 * @example
 * export const [useThing, ThingContext] = generateContext<typeof Thing>()
 * @example
 * import { ThingContext } from './ThingContext'
 * import { createThing } from './thing'
 * ...
 * export const ThingProvider = ({ children }) => (
 *  <ThingContext value={createThing()}>
 *   {children}
 *  </ThingContext>
 * )
 * @example
 * import { useThing } from './ThingContext'
 * ...
 * export const Component = () => {
 *  const thing = useThing()
 *  ...
 * }
 */

export function generateContext<T extends unknown | null>(): readonly [() => T, Provider<T | undefined>] {
  const Ctx = createContext<T | undefined>(undefined)

  const useCtx = () => {
    const c = useContext(Ctx)
    if (c === undefined) throw new Error('useContext must be inside a Provider with a value.')
    return c
  }
  return [useCtx, Ctx.Provider] as const
}
